import { useEffect, useState, useContext, useCallback } from 'react';
import { Skeleton } from 'primereact/skeleton';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import SessionCard from './SessionCard';
import AuthContext from '../../../../auth/AuthContext';
import AlertService from '../../../../services/AlertService';
import QuizService from '../../../../services/QuizService';
import { useBreadcrumb } from '../../../../context/BreadcrumbContext';

const QuizSessions = () => {
  const { id } = useParams();
  const authContext = useContext(AuthContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [sessions, setSessions] = useState([]);
  const { setDynamicLabels } = useBreadcrumb();
  const location = useLocation();

  useEffect(() => {
    const quizAccess = authContext.getPermissions('quizmaster');
    if (quizAccess.filter((f) => f.access.includes(1)).length === 0) {
      navigate('/');
    }
  }, [authContext, navigate]);
  useEffect(() => {
    const quizName = location.state?.quizName;

    if (quizName) {
      setDynamicLabels((prev) => ({
        ...prev,
        [id]: quizName,
      }));
    }
  }, [id, location.state, setDynamicLabels]);

  const fetchSessions = useCallback(async () => {
    try {
      setLoading(true);

      const res = await QuizService.GetQuizSessions(id, '', authContext);

      const list = Array.isArray(res?.data?.results) ? res.data.results : [];

      const mapped = list.map((s) => ({
        id: s.id,
        userId: s.userId,
        attendeeName: s.attendeeName,
        startDate: s.startDate,
      }));

      setSessions(mapped);
    } catch (err) {
      console.error('Failed to fetch quiz sessions', err);
      setSessions([]);
    } finally {
      setLoading(false);
    }
  }, [id, authContext]);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  const deleteSession = async (userId) => {
    try {
      await QuizService.RemoveQuizSession(id, userId, authContext);

      setSessions((prev) => prev.filter((s) => s.userId !== userId));

      AlertService.success('Session killed successfully');
    } catch (err) {
      console.error('Failed to delete session', err);
      AlertService.error('Failed to kill session');
    }
  };

  return (
    <div>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
        {loading &&
          Array.from({ length: 8 }).map((_, idx) => (
            <div
              key={idx}
              className="bg-white dark:bg-slate-900 rounded-md shadow flex flex-col h-full"
            >
              <div className="pt-6 flex justify-center">
                <Skeleton
                  width="60%"
                  height="1.25rem"
                  className="!bg-slate-200 dark:!bg-slate-700"
                />
              </div>

              <div className="mt-4 mb-6 flex justify-center">
                <Skeleton
                  width="70%"
                  height="1rem"
                  className="!bg-slate-200 dark:!bg-slate-700"
                />
              </div>

              <div className="mt-auto bg-slate-50 dark:bg-slate-800 py-3 flex justify-center">
                <Skeleton
                  width="40%"
                  height="1rem"
                  className="!bg-slate-200 dark:!bg-slate-700"
                />
              </div>
            </div>
          ))}

        {!loading &&
          sessions.map((session) => (
            <SessionCard
              key={session.id}
              data={session}
              onDelete={deleteSession}
            />
          ))}
      </div>

      {!loading && sessions.length === 0 && (
        <p className="text-center text-gray-500 mt-10">No sessions available</p>
      )}
    </div>
  );
};

export default QuizSessions;
